import { createContext, use } from "react";
import type { FC, ReactNode } from "react";
import { useParsedParams } from "../shared/useParsedParams";
import { z } from "zod";
import { ensureUserInfo, type UserInfo, type UserInfoOrganisation, useUserInfo } from "../api/userInfo";
import { LayoutWithoutSelectedOrganisation } from "./layout/Layout";
import { ErrorNotFound } from "./error/ErrorPage";

const findOrganisationBySlug = (
  { customers }: UserInfo,
  organisationSlug: string
): UserInfoOrganisation | undefined => {
  for (const customer of customers) {
    if (customer.slug === organisationSlug) return customer;

    if (customer.customerKind === "enterprise") {
      const organisation = customer.organisations.find(({ slug }) => slug === organisationSlug);
      if (organisation) return organisation;
    }
  }
};

const getCustomerFromOrganisation = ({ customers }: UserInfo, organisation: UserInfoOrganisation) => {
  const customer = customers.find(
    customer =>
      customer.id === organisation.id ||
      (customer.customerKind === "enterprise" && customer.organisations.some(({ id }) => id === organisation.id))
  );

  if (!customer) throw new Error(`No customer holds the current organisation "${organisation.slug}"`);

  return customer;
};

const CurrentOrganisationContext = createContext<UserInfoOrganisation | undefined>(undefined);

type CurrentOrganisationProviderProps = { children: ReactNode };

export const CurrentOrganisationProvider: FC<CurrentOrganisationProviderProps> = ({ children }) => {
  const userInfo = useUserInfo();
  const parsedParams = useParsedParams(
    z.object({ organisationSlug: z.string() }),
    "Missing organisation slug param(:organisationSlug) in pathname"
  );

  const currentOrganisation = findOrganisationBySlug(userInfo, parsedParams.organisationSlug);

  if (!currentOrganisation)
    return (
      <LayoutWithoutSelectedOrganisation>
        <ErrorNotFound isAuthenticated={true} />
      </LayoutWithoutSelectedOrganisation>
    );

  return <CurrentOrganisationContext value={currentOrganisation}>{children}</CurrentOrganisationContext>;
};

export const useCurrentOrganisation = () => {
  const context = use(CurrentOrganisationContext);
  if (!context) throw new Error('"useCurrentOrganisation" can only be used within a "CurrentOrganisationProvider" ');

  return context;
};

export const useCurrentCustomer = () => getCustomerFromOrganisation(useUserInfo(), useCurrentOrganisation());

const getCurrentOrganisationSlug = () => {
  const [, slug] = location.pathname.split("/");

  if (!slug) throw new Error("No organisation(:organisationSlug) slug found in pathname");

  return slug;
};

export const getCurrentOrganisation = async () => {
  const userInfo = await ensureUserInfo();
  const slug = getCurrentOrganisationSlug();

  if (!userInfo) return null;

  const currentOrganisation = findOrganisationBySlug(userInfo, slug);

  if (!currentOrganisation) return null;
  return currentOrganisation;
};
